import type { LeaderboardMessage, Results } from "@agency/shared";
import { net } from "../net/NetClient.js";
import { store } from "../store/GameStore.js";
import { clear, el, formatSeconds } from "./dom.js";

export class LeaderboardPanel {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly tabs: HTMLElement;
  private readonly allTimeTab: HTMLButtonElement;
  private readonly lastTab: HTMLButtonElement;
  private current: LeaderboardMessage = { allTime: [] };
  private tab: "alltime" | "last" = "alltime";
  private visible = false;

  constructor(parent: HTMLElement) {
    this.root = el("div", "panel side-panel hidden");
    this.tabs = el("div", "tabs");
    this.allTimeTab = el("button", "tab active", "All-time");
    this.lastTab = el("button", "tab", "Last project");
    this.allTimeTab.type = "button";
    this.lastTab.type = "button";
    this.allTimeTab.addEventListener("click", () => this.setTab("alltime"));
    this.lastTab.addEventListener("click", () => this.setTab("last"));
    this.tabs.append(this.allTimeTab, this.lastTab);
    this.body = el("div");
    this.root.append(this.tabs, this.body);
    parent.appendChild(this.root);
  }

  mount(): void {
    store.on("leaderboard", (message) => {
      this.current = message;
      this.render();
    });
    store.on("results", (results: Results) => {
      this.current = { ...this.current, lastResults: results };
      this.render();
    });
  }

  toggle(): void {
    this.visible = !this.visible;
    this.root.classList.toggle("hidden", !this.visible);
    if (this.visible) net.requestLeaderboard();
  }

  hide(): void {
    this.visible = false;
    this.root.classList.add("hidden");
  }

  private setTab(tab: "alltime" | "last"): void {
    this.tab = tab;
    this.allTimeTab.classList.toggle("active", tab === "alltime");
    this.lastTab.classList.toggle("active", tab === "last");
    this.render();
  }

  private render(): void {
    clear(this.body);
    if (this.tab === "alltime") {
      this.renderRows(
        ["#", "Dev", "Score", "Projects"],
        this.current.allTime.map((row, index) => [
          String(index + 1),
          row.nickname,
          String(row.totalScore),
          String(row.projectsContributed),
        ]),
        "No scores yet. Be the first!",
      );
    } else {
      const entries = this.current.lastResults?.entries ?? [];
      this.renderRows(
        ["#", "Dev", "Worked", "Share"],
        entries.map((e) => [
          String(e.rank),
          e.nickname + (e.mvp ? " ★" : "") + (e.present ? "" : " 🚪"),
          formatSeconds(e.workSeconds),
          `${e.sharePct.toFixed(1)}%`,
        ]),
        "No completed project yet.",
      );
    }
  }

  private renderRows(headers: string[], rows: string[][], empty: string): void {
    if (rows.length === 0) {
      this.body.appendChild(el("div", "server-line", empty));
      return;
    }
    const table = el("table", "results");
    const thead = el("thead");
    const headRow = el("tr");
    for (const h of headers) headRow.appendChild(el("th", undefined, h));
    thead.appendChild(headRow);
    const tbody = el("tbody");
    for (const row of rows) {
      const tr = el("tr");
      for (const cell of row) tr.appendChild(el("td", undefined, cell));
      tbody.appendChild(tr);
    }
    table.append(thead, tbody);
    this.body.appendChild(table);
  }
}
